import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { LeadFilters } from '@/features/leads/lead-filters';
import { toLeadQuery } from '@/features/leads/lead-filters';
import { api } from './client';
import { qk } from './keys';
import type {
  Category,
  Country,
  EstimateRequest,
  EstimateResponse,
  Job,
  JobCreate,
  LeadPage,
  Quota,
  Region,
} from './types';

export function useQuota() {
  return useQuery({
    queryKey: qk.quota(),
    queryFn: () => api<Quota>('/quota'),
    // Le quota bouge à chaque appel RapidAPI ; le SSE le rafraîchit pendant
    // un run, 30 s de fraîcheur suffisent au repos.
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useCountries() {
  return useQuery({
    queryKey: qk.countries(),
    queryFn: () => api<Country[]>('/geo/countries'),
    staleTime: Infinity, // la liste des pays ne bouge pas dans une session
  });
}

export function useRegions(cc: string | null) {
  return useQuery({
    queryKey: qk.regions(cc ?? ''),
    queryFn: () => api<Region[]>(`/geo/countries/${cc}/regions`),
    enabled: Boolean(cc),
    staleTime: Infinity,
  });
}

export function useCategorySearch(q: string) {
  return useQuery({
    queryKey: qk.categories(q),
    queryFn: () => api<Category[]>('/categories', { query: { q } }),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60_000,
    // Garder la page précédente évite le clignotement de la combobox à
    // chaque caractère frappé.
    placeholderData: (prev) => prev,
  });
}

export function useEstimate(body: EstimateRequest | null) {
  return useQuery({
    queryKey: ['estimate', body],
    queryFn: () => api<EstimateResponse>('/jobs/estimate', { method: 'POST', body: body! }),
    enabled: body !== null,
    // L'estimation ne consomme aucune requête RapidAPI : on peut la relancer
    // librement, mais 15 s de cache évitent une rafale pendant la saisie.
    staleTime: 15_000,
  });
}

export function useJobs() {
  return useQuery({
    queryKey: qk.jobs(),
    queryFn: () => api<Job[]>('/jobs'),
    // Filet de sécurité si le SSE tombe : il pousse déjà à 1 Hz quand il marche.
    refetchInterval: 20_000,
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: qk.job(id),
    queryFn: () => api<Job>(`/jobs/${id}`),
    refetchInterval: 20_000,
  });
}

export function useCreateJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: JobCreate) => api<Job>('/jobs', { method: 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.jobs() });
      void client.invalidateQueries({ queryKey: qk.quota() });
    },
  });
}

export function useCancelJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Job>(`/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: (job) => {
      client.setQueryData(qk.job(job.id), job);
      void client.invalidateQueries({ queryKey: qk.jobs() });
    },
  });
}

export function useResumeJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Job>(`/jobs/${id}/resume`, { method: 'POST' }),
    onSuccess: (job) => {
      client.setQueryData(qk.job(job.id), job);
      void client.invalidateQueries({ queryKey: qk.jobs() });
    },
  });
}

export function useLeads(filters: LeadFilters) {
  return useInfiniteQuery({
    queryKey: qk.leads(filters),
    initialPageParam: null as string | null,
    // Pagination KEYSET : le curseur vient du serveur, jamais un OFFSET.
    queryFn: ({ pageParam }) =>
      api<LeadPage>('/leads', { query: toLeadQuery(filters, pageParam) as never }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
